import { ApiService } from '~/services/api/api'
import type { ShareableEntityList } from '~/types/ShareableEntityList'
import { type PopulatedSimulation, type Simulation } from '~/types/Simulation'

export class SimulationService {
  private readonly apiService = new ApiService()

  async getSimulations(queryParameters: Record<string, string> = {}) {
    const response = await this.apiService.get<Simulation[]>('/simulations', {
      params: queryParameters,
    })
    return response
  }

  // TODO: add auth to deep link page and then remove fallback auth from this method
  async getWorkspaceSimulations(workspaceId: string) {
    const response = await this.apiService.get<ShareableEntityList<Simulation>>(
      `/workspaces/${workspaceId}/simulations`,
      {
        useFallbackAuth: true,
      },
    )
    return response
  }

  async getSimulationById(simulationId: string) {
    const response = await this.apiService.get<Simulation>(
      `simulations/${simulationId}`,
    )
    return response
  }

  async getPopulatedSimulationById(simulationId: string) {
    const response = await this.apiService.get<PopulatedSimulation>(
      `simulations/${simulationId}`,
      {
        params: {
          populate:
            'character,stockDirectEmails.characters,stockDirectChats.characters,environment',
        },
        useFallbackAuth: true,
      },
    )
    return response
  }

  async postSimulation(body: Partial<Simulation>) {
    const response = await this.apiService.post<
      Simulation,
      Partial<Simulation>
    >('/simulations', body)
    return response
  }

  async updateSimulationById(simulationId: string, body: Partial<Simulation>) {
    const response = await this.apiService.patch<
      Simulation,
      Partial<Simulation>
    >(`/simulations/${simulationId}`, body)
    return response
  }

  async deleteSimulationById(simulationId: string) {
    const response = await this.apiService.delete<undefined>(
      `/simulations/${simulationId}`,
    )
    return response
  }

  async publishSimulationById(simulationId: string) {
    const response = await this.apiService.post<
      Simulation,
      Record<string, never>
    >(`/simulations/${simulationId}/publish`, {})
    return response
  }

  async unpublishSimulationById(simulationId: string) {
    const response = await this.apiService.post<
      Simulation,
      Record<string, never>
    >(`/simulations/${simulationId}/unpublish`, {})
    return response
  }

  async getWorkspaceSimulationUpdates(
    workspaceId: string,
    updatedAtGt: string,
  ) {
    const response = await this.apiService.get<ShareableEntityList<Simulation>>(
      `/workspaces/${workspaceId}/simulations`,
      {
        params: {
          updatedAtGt,
        },
      },
    )
    return response
  }
}
